import type { InstalacionDeInforme } from "@/lib/informes";
import type { ObraPropuesta } from "@/lib/rellenoConIa";
import { textoDeDecimal, textoDeNumero } from "@/lib/montos";

import type { FormularioDeInstalacion } from "./instalaciones";

/* ---------------------------------------------------------------------------
   "Rellenar con IA" en "Instalaciones y obras complementarias": traducir lo que
   propuso el modelo al formulario de la pestaña.

   ES EL GEMELO DE `maquinariasConIa.ts` y existe por lo mismo: esta pestaña
   tampoco tiene borrador —de cada obra cuelgan fotos, así que cada fila se crea
   con su propio POST— y el riesgo real es el punto de miles. Un "1.000,50" que
   llegue al `<input>` con punto decimal vuelve al servidor valiendo mil coma
   cinco… o mil uno, según cómo lo lea. En un informe pericial.

   ══ NO SE EXTRAJO UN MÓDULO COMÚN CON MAQUINARIA, y es a conciencia ══════════

   Lo que las dos tienen igual es el PATRÓN (traducir, comparar por nombre,
   resumir), no el código: los campos son otros, los defaults son otros
   —instalaciones no tiene ninguno— y el resumen nombra otra cosa. Un módulo
   compartido sería tres funciones con un `if` adentro por cada diferencia. Lo
   que sí se compartió es lo que de verdad se repetía: el PANEL
   (`FilasPropuestas`), que es donde viven las decisiones.
--------------------------------------------------------------------------- */

/**
 * Una obra propuesta, con la forma que tiene el formulario de la pestaña.
 *
 * NO TIENE NINGÚN DEFAULT QUE APLICAR, al revés que maquinaria —que pone
 * `unidades` en 1 y `moneda` en USD—: acá el request solo exige el `tipo`, y ese
 * viene del modelo o la fila se descartó en el servidor. `INSTALACION_VACIA`
 * tiene todos sus campos en blanco justamente por eso.
 *
 * `nota_terreno` VA VACÍA SIEMPRE: el servidor no la propone —es lo que anotó
 * quien fue a ver la obra— así que acá no hay nada que traducir. Se escribe en
 * blanco y no se omite porque el formulario espera el campo.
 */
export function formularioDesdeLaObraPropuesta(obra: ObraPropuesta): FormularioDeInstalacion {
  return {
    tipo: obra.tipo,
    anio: textoDeNumero(obra.anio),
    cantidad: decimal(obra.cantidad),
    valor_reposicion_uf_un: decimal(obra.valor_reposicion_uf_un),
    valor_comercial_uf_un: decimal(obra.valor_comercial_uf_un),
    nota_terreno: "",
  };
}

/**
 * ¿Esta obra ya está en el cuadro?
 *
 * NO SIRVE PARA ESCONDERLA: el panel la lista igual y solo apaga su botón.
 *
 * SE COMPARA SOLO EL TIPO, igual que maquinaria compara solo el nombre. Acá el
 * argumento es un poco más flojo —un predio SÍ puede tener dos cercos
 * distintos— pero el documento los nombraría distinto ("Cerco perimetral" y
 * "Cerco divisorio"), y la alternativa es peor: sumando el año o la cantidad,
 * una cifra mal leída haría pasar por nueva una obra que ya está, y el duplicado
 * en un cuadro que se valoriza no se ve.
 */
export function yaEstaEnElCuadroDeObras(
  tipo: string,
  guardadas: InstalacionDeInforme[],
): boolean {
  const clave = normalizar(tipo);

  return guardadas.some((obra) => normalizar(obra.tipo) === clave);
}

/**
 * Qué decir después del clic.
 *
 * EL VERBO ES "ENCONTRAMOS" Y NO "SE AGREGARON", igual que en maquinaria y al
 * revés que en aguas y suelos: acá todavía no se agregó nada, porque cada obra
 * se agrega con su propio botón.
 */
export function resumenDeLoPropuestoEnInstalaciones(
  obras: number,
  descripcion: boolean,
): string {
  if (obras === 0 && !descripcion) {
    // A DIFERENCIA DE MAQUINARIA, acá no hace falta explicar que es lo
    // habitual: el catálogo SÍ tiene un documento para esta pestaña —la carpeta
    // interna, que pide literalmente el "inventario de construcciones,
    // instalaciones y obras complementarias"—, así que una propuesta vacía
    // significa que ese papel no está o que no las enumera. Eso ya lo dice la
    // lista de documentos leídos que va al lado.
    return "No encontramos instalaciones ni obras en los documentos leídos.";
  }

  const partes: string[] = [];

  if (obras > 0) {
    partes.push(`${obras} ${obras === 1 ? "instalación u obra" : "instalaciones u obras"}`);
  }

  if (descripcion) partes.push("la descripción general");

  return `Encontramos ${partes.join(" y ")}.`;
}

/** Un decimal del modelo, escrito como lo escribiría una persona. */
function decimal(valor: number | null): string {
  return valor === null ? "" : textoDeDecimal(String(valor));
}

function normalizar(tipo: string): string {
  return tipo.trim().toLocaleLowerCase("es").replace(/\s+/g, " ");
}
